import { Component } from '@theme/component';

const STEPS = ['cleanse', 'serum', 'moisturise', 'sun'];
const SKIN_TYPES = ['dry', 'oily', 'combination', 'normal', 'sensitive', 'not_sure'];
const CONCERNS = ['simple', 'moisture', 'dull-looking-skin', 'blemishes', 'fine-lines', 'uneven-tone'];
const TEXTURES = ['any', 'gel', 'cream', 'oil', 'foam'];

function safeProduct(value, origin) {
  if (!value || !STEPS.includes(value.step) || !TEXTURES.concat(['serum', 'essence', 'ampoule']).includes(value.texture)) return null;
  if (typeof value.title !== 'string' || !value.title.trim() || typeof value.reason !== 'string' || !value.reason.trim()) return null;
  if (!Number.isSafeInteger(value.price) || value.price < 0 || typeof value.money !== 'string') return null;
  if (typeof value.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,199}$/.test(value.handle)) return null;
  let url;
  try {
    url = new URL(value.url, origin);
    if (url.origin !== origin || !url.pathname.endsWith('/products/' + value.handle) || url.hash ||
      !/^\?variant=\d+$/.test(url.search)) return null;
  } catch { return null; }
  let image = '';
  if (typeof value.image === 'string' && value.image) {
    try {
      const photo = new URL(value.image, origin);
      if (photo.protocol === 'https:' && !photo.username && !photo.password &&
        (photo.origin === origin || photo.hostname === 'cdn.shopify.com')) image = photo.href;
    } catch { /* Text/product link still works without a photo. */ }
  }
  const skinTypes = typeof value.skinTypes === 'string' ? value.skinTypes.split(',').map(type => type.trim().toLowerCase())
    .filter(type => SKIN_TYPES.includes(type) || type === 'all') : [];
  return {
    handle: value.handle, title: value.title.slice(0, 500), url: url.pathname + url.search, image,
    price: value.price, money: value.money.slice(0, 100), variant: typeof value.variant === 'string' ? value.variant.slice(0, 300) : '',
    step: value.step, texture: value.texture, skinTypes,
    goals: Array.isArray(value.goals) ? value.goals.filter(goal => CONCERNS.includes(goal)) : [],
    reason: value.reason.slice(0, 2000),
    directions: typeof value.directions === 'string' ? value.directions.slice(0, 2000) : '',
    precautions: typeof value.precautions === 'string' ? value.precautions.slice(0, 2000) : ''
  };
}

function selectRoutine(products, answers) {
  const candidates = products.filter(product => {
    // A general or untagged product is not evidence of sensitive-skin suitability.
    // The merchant must explicitly verify and tag that match before it is recommended.
    const skinMatch = answers.skin === 'sensitive'
      ? product.skinTypes.includes('sensitive')
      : (answers.skin === 'not_sure' || product.skinTypes.length === 0 || product.skinTypes.includes('all') || product.skinTypes.includes(answers.skin));
    return (answers.budget === 0 || product.price <= answers.budget) && skinMatch;
  });
  return STEPS.map(step => {
    if (step === 'serum' && (answers.skin === 'sensitive' || answers.concern === 'simple')) return { step, product: null, skipped: true };
    let pool = candidates.filter(product => product.step === step);
    if (step === 'moisturise' && ['gel', 'cream'].includes(answers.texture)) pool = pool.filter(product => product.texture === answers.texture);
    if (step === 'cleanse' && ['oil', 'foam'].includes(answers.texture)) pool = pool.filter(product => product.texture === answers.texture);
    // Optional targeted steps require an explicitly verified concern match.
    if (step === 'serum') pool = pool.filter(product => product.goals.includes(answers.concern));
    pool.sort((a, b) => Number(b.goals.includes(answers.concern)) - Number(a.goals.includes(answers.concern)) || a.price - b.price);
    return { step, product: pool[0] || null, skipped: false };
  });
}

class SacredSkinFinder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const controller = this.controller;
    queueMicrotask(() => { if (this.controller === controller && this.isConnected) this.setup(); });
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.setup();
  }

  setup() {
    this.form = this.querySelector('[data-finder-form]');
    this.result = this.querySelector('[data-finder-result]');
    this.cards = this.querySelector('[data-finder-cards]');
    this.status = this.querySelector('[data-finder-status]');
    this.submitButton = this.querySelector('[data-finder-submit]');
    this.dataNode = this.querySelector('[data-finder-data]');
    if (!this.form || !this.result || !this.cards || !this.status || !this.submitButton || !this.dataNode) return;
    try {
      const data = JSON.parse(this.dataNode.textContent);
      this.strings = data.strings || {};
      this.products = Array.isArray(data.products) ? data.products.slice(0, 40).map(value => safeProduct(value, location.origin)).filter(Boolean) : [];
    } catch {
      this.status.textContent = this.dataset.loadError || '';
      return;
    }
    const signal = this.controller.signal;
    this.form.addEventListener('submit', event => this.showResults(event), { signal });
    this.querySelector('[data-finder-edit]')?.addEventListener('click', () => this.editAnswers(false), { signal });
    this.querySelector('[data-finder-restart]')?.addEventListener('click', () => this.editAnswers(true), { signal });
    this.submitButton.disabled = false;
    if (this.products.length === 0) this.status.textContent = this.strings.empty_catalog || '';
  }

  showResults(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;
    const data = new FormData(this.form);
    const answers = { skin: data.get('skin'), concern: data.get('concern'), texture: data.get('texture'), budget: Number(data.get('budget')) };
    if (!SKIN_TYPES.includes(answers.skin) || !CONCERNS.includes(answers.concern) || !TEXTURES.includes(answers.texture) ||
      ![0, 10000, 20000, 40000, 60000].includes(answers.budget)) return;
    const selected = selectRoutine(this.products, answers);
    this.cards.replaceChildren();
    let matches = 0;
    for (const item of selected) {
      if (item.skipped) continue;
      this.cards.append(this.makeCard(item));
      if (item.product) matches++;
    }
    if (!matches) this.cards.replaceChildren();
    const guidance = this.querySelector('[data-finder-guidance]');
    if (guidance) guidance.textContent = answers.skin === 'sensitive' ? this.strings.sensitive_guidance : this.strings.general_guidance;
    const targetNote = this.querySelector('[data-finder-target-note]');
    if (targetNote) {
      targetNote.textContent = this.strings.no_targeted_match;
      targetNote.hidden = !matches || answers.concern === 'simple' || selected.some(item => item.product?.goals.includes(answers.concern));
    }
    const note = this.querySelector('[data-finder-selection-note]');
    if (note) note.textContent = matches ? this.strings.selection_note : this.strings.no_results;
    this.form.hidden = true;
    this.result.hidden = false;
    this.status.textContent = matches ? this.strings.results_ready : this.strings.no_results;
    this.querySelector('[data-finder-results-heading]')?.focus();
  }

  makeCard({ step, product }) {
    const card = document.createElement('li');
    card.className = 'ssc-finder-card' + (product ? '' : ' ssc-finder-card--empty');
    const label = document.createElement('p'); label.className = 'ssc-finder-card__step';
    label.textContent = this.strings['step_' + step] || step; card.append(label);
    if (!product) {
      const empty = document.createElement('p'); empty.textContent = this.strings['missing_' + step] || this.strings.no_results;
      card.append(empty); return card;
    }
    const link = document.createElement('a'); link.className = 'ssc-finder-card__product'; link.href = product.url;
    if (product.image) {
      const image = document.createElement('img'); image.src = product.image; image.alt = ''; image.width = 480; image.height = 480;
      image.loading = 'lazy'; image.decoding = 'async';
      image.addEventListener('error', () => image.remove(), { once: true }); link.append(image);
    }
    const title = document.createElement('h3'); title.textContent = product.title; link.append(title); card.append(link);
    if (product.variant) {
      const variant = document.createElement('p'); variant.className = 'ssc-finder-card__variant'; variant.textContent = product.variant; card.append(variant);
    }
    const price = document.createElement('p'); price.className = 'ssc-finder-card__price'; price.textContent = product.money; card.append(price);
    const reason = document.createElement('p'); reason.textContent = product.reason; card.append(reason);
    if (product.directions || product.precautions) {
      const details = document.createElement('details');
      const summary = document.createElement('summary'); summary.textContent = this.strings.usage_and_notes; details.append(summary);
      for (const text of [product.directions, product.precautions]) {
        if (!text) continue;
        const paragraph = document.createElement('p'); paragraph.textContent = text; details.append(paragraph);
      }
      card.append(details);
    }
    const open = document.createElement('a'); open.href = product.url; open.className = 'ssc-finder-link'; open.textContent = this.strings.view_product;
    open.setAttribute('aria-label', this.strings.view_product + ': ' + product.title); card.append(open);
    return card;
  }

  editAnswers(reset) {
    if (reset) {
      this.form.reset();
      this.cards.replaceChildren();
    }
    this.result.hidden = true;
    this.form.hidden = false;
    this.status.textContent = reset ? this.strings.restarted : this.strings.editing;
    const first = this.form.querySelector('input:checked') || this.form.querySelector('input');
    first?.focus();
  }
}

if (!customElements.get('sacred-skin-finder')) customElements.define('sacred-skin-finder', SacredSkinFinder);
